// Canonical plan limits for anything the UI advertises.
//
// These numbers previously lived as hardcoded strings inside Subscription,
// PaywallModal, LoginOfferPopup, BookGenerator and Features. They drifted
// three separate times:
//   - Writer's "50 pages per day" was wrong for months (actually 100).
//   - Author 200 / Pro 500 / Pro Max 1,500 survived the 2026-09-08 raise to
//     300 / 800 / 2,000 in four different components.
//   - The pricing page never mentioned book length at all, which by then was
//     the headline difference between tiers.
//
// MUST stay in step with server/services/planLimits.js PLAN_LIMITS. When a
// limit changes there, change it here in one place. Server-side enforcement
// always wins — this exists so the marketing never promises more (or less)
// than the backend actually grants.
//
// Anything reading plan.max_pages_per_book from the API is already correct:
// subscription_plans is synced by migration 022. Use these constants only
// where a component has no API-backed plan object to hand.

export type PlanKey = 'starter' | 'writer' | 'author' | 'pro' | 'promax';

export interface PlanLimit {
  maxPagesPerBook: number;
  pagesPerMonth: number;
  pagesPerDay: number;
  booksPerMonth: number | 'unlimited';
}

export const PLAN_LIMITS: Record<PlanKey, PlanLimit> = {
  starter: { maxPagesPerBook: 25,   pagesPerMonth: 50,    pagesPerDay: 30,    booksPerMonth: 1 },
  writer:  { maxPagesPerBook: 100,  pagesPerMonth: 500,   pagesPerDay: 100,   booksPerMonth: 5 },
  author:  { maxPagesPerBook: 300,  pagesPerMonth: 2000,  pagesPerDay: 300,   booksPerMonth: 15 },
  pro:     { maxPagesPerBook: 800,  pagesPerMonth: 10000, pagesPerDay: 1000,  booksPerMonth: 'unlimited' },
  promax:  { maxPagesPerBook: 2000, pagesPerMonth: 25000, pagesPerDay: 2000,  booksPerMonth: 'unlimited' },
};

// Matches server/services/pageUsage.js WORDS_PER_PAGE.
export const WORDS_PER_PAGE = 325;

// Matches FREE_PAGE_LIMIT in server/routes/humanize.js for signed-in
// Starter and monthly Writer accounts. Book-generation pages are separate.
export const BASIC_HUMANIZER_PAGES_PER_MONTH = 10;

const fmt = (n: number) => n.toLocaleString();

/** e.g. "Up to 2,000 pages per book" */
export function pagesPerBookLabel(plan: PlanKey): string {
  return `Up to ${fmt(PLAN_LIMITS[plan].maxPagesPerBook)} pages per book`;
}

/** e.g. "~650k words" — book length means more to an author in words. */
export function wordsPerBookLabel(plan: PlanKey): string {
  const words = PLAN_LIMITS[plan].maxPagesPerBook * WORDS_PER_PAGE;
  return `~${fmt(Math.round(words / 1000))}k words`;
}

/** e.g. "2,000 pages/month" */
export function pagesPerMonthLabel(plan: PlanKey): string {
  return `${fmt(PLAN_LIMITS[plan].pagesPerMonth)} pages/month`;
}

/** e.g. "15 books / month", "1 book / month" or "Unlimited books" */
export function booksPerMonthLabel(plan: PlanKey): string {
  const b = PLAN_LIMITS[plan].booksPerMonth;
  if (b === 'unlimited') return 'Unlimited books';
  // Starter is 1, and "1 books / month" on a signup surface reads like a bug.
  return `${b} book${b === 1 ? '' : 's'} / month`;
}
